import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import {
  useSchedules,
  useUpdateSchedule,
  useDeleteSchedule,
  useSchedulePreview,
  type Schedule,
} from '../../api/schedules.ts';
import { useScreens } from '../../api/screens.ts';
import { useGroups } from '../../api/groups.ts';
import { usePlaylists } from '../../api/playlists.ts';
import { useLayouts } from '../../api/layouts.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Badge, Loading, ErrorState, EmptyState } from '../../components/ui.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { scheduleWindowSummary } from '../../lib/schedule-summary.ts';
import { zonedNow } from '../../lib/tz.ts';
import { ApiError } from '../../api/client.ts';
import { ScheduleIcon } from '../../onboarding/icons.tsx';
import { ScheduleForm, type ScheduleInitial } from './ScheduleForm.tsx';
import { WeekCalendar } from './WeekCalendar.tsx';
import { TargetPicker, type TargetOption } from './TargetPicker.tsx';
import { winnerScheduleAt, type CalendarSource } from './calendar.ts';

export interface SchedulesSearch {
  edit?: string;
  create?: boolean;
}

type ViewMode = 'table' | 'week';
const VIEW_KEY = 'seencast.schedules.view';
const TARGET_KEY = 'seencast.schedules.calTarget';

function readLS(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLS(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function SchedulesPage() {
  const search = useSearch({ strict: false }) as SchedulesSearch;
  const navigate = useNavigate();
  const perms = usePermissions();

  const schedules = useSchedules();
  const screens = useScreens();
  const groups = useGroups();
  const playlists = usePlaylists();
  const layouts = useLayouts();
  const update = useUpdateSchedule();
  const del = useDeleteSchedule();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // View + calendar target, both persisted across visits.
  const [view, setView] = useState<ViewMode>(() => (readLS(VIEW_KEY) === 'week' ? 'week' : 'table'));
  const [target, setTarget] = useState<string | null>(() => readLS(TARGET_KEY));
  useEffect(() => writeLS(VIEW_KEY, view), [view]);
  useEffect(() => {
    if (target) writeLS(TARGET_KEY, target);
  }, [target]);

  // Prefill carried into the create form when a calendar slot is clicked.
  const [slotInitial, setSlotInitial] = useState<ScheduleInitial | null>(null);

  const setSearch = (patch: Partial<SchedulesSearch>) =>
    navigate({ to: '/schedules', search: (prev) => ({ ...prev, ...patch }) });

  const playlistName = (id: string) => playlists.data?.find((p) => p.id === id)?.name ?? '(deleted)';
  const layoutName = (id: string) => layouts.data?.find((l) => l.id === id)?.name ?? 'a layout';

  const buckets = useMemo(() => {
    const list = schedules.data ?? [];
    const map = new Map<string, { label: string; order: number; rows: Schedule[] }>();
    for (const s of list) {
      const key = `${s.target_type}:${s.target_id ?? 'org'}`;
      if (!map.has(key)) {
        let label = 'Whole organization';
        let order = 0;
        if (s.target_type === 'group') {
          label = `Group · ${groups.data?.find((g) => g.id === s.target_id)?.name ?? 'Unknown'}`;
          order = 1;
        } else if (s.target_type === 'screen') {
          label = `Screen · ${screens.data?.find((x) => x.id === s.target_id)?.name ?? 'Unknown'}`;
          order = 2;
        }
        map.set(key, { label, order, rows: [] });
      }
      map.get(key)!.rows.push(s);
    }
    return [...map.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [schedules.data, groups.data, screens.data]);

  // --- Calendar target options + default -----------------------------------------
  const targetOptions = useMemo<TargetOption[]>(() => {
    const opts: TargetOption[] = [];
    for (const g of groups.data ?? [])
      opts.push({ id: `group:${g.id}`, label: g.name, kind: 'group', sub: `${g.screen_ids.length} screens` });
    for (const s of screens.data ?? [])
      opts.push({ id: `screen:${s.id}`, label: s.name, kind: 'screen', sub: s.location ?? undefined });
    return opts;
  }, [groups.data, screens.data]);

  // Default the target to the first screen once data lands.
  useEffect(() => {
    if (!target && targetOptions.length > 0) {
      const firstScreen = targetOptions.find((o) => o.kind === 'screen') ?? targetOptions[0];
      setTarget(firstScreen!.id);
    }
  }, [target, targetOptions]);

  const parsedTarget = useMemo(() => {
    if (!target || !targetOptions.some((o) => o.id === target)) return null; // unset or stale (deleted)
    const [kind, rawId] = target.split(':');
    return { kind: kind as 'screen' | 'group', rawId: rawId! };
  }, [target, targetOptions]);

  // Schedules that actually resolve for the picked target (matches the API
  // resolver's candidate set): org + this target + (for a screen) its groups.
  const calendarSources = useMemo<CalendarSource[]>(() => {
    if (!parsedTarget || !schedules.data) return [];
    const nameOf = (id: string) => playlists.data?.find((p) => p.id === id)?.name ?? '(deleted)';
    const memberGroupIds =
      parsedTarget.kind === 'screen'
        ? (groups.data ?? []).filter((g) => g.screen_ids.includes(parsedTarget.rawId)).map((g) => g.id)
        : [];
    const applies = (s: Schedule) => {
      if (!s.enabled) return false;
      if (s.target_type === 'org') return true;
      if (parsedTarget.kind === 'screen') {
        if (s.target_type === 'screen') return s.target_id === parsedTarget.rawId;
        return s.target_type === 'group' && !!s.target_id && memberGroupIds.includes(s.target_id);
      }
      return s.target_type === 'group' && s.target_id === parsedTarget.rawId;
    };
    return schedules.data.filter(applies).map((s) => ({
      id: s.id,
      playlist_id: s.playlist_id,
      playlist_name: nameOf(s.playlist_id),
      days_mask: s.days_mask,
      start_time: s.start_time,
      end_time: s.end_time,
      priority: s.priority,
      target_type: s.target_type,
      created_at: s.created_at,
    }));
  }, [parsedTarget, schedules.data, groups.data, playlists.data]);

  // Timezone to render in, plus a note when a group spans multiple zones.
  const { tz, tzNote } = useMemo(() => {
    if (!parsedTarget) return { tz: 'UTC', tzNote: null as string | null };
    if (parsedTarget.kind === 'screen') {
      const sc = screens.data?.find((s) => s.id === parsedTarget.rawId);
      return { tz: sc?.timezone ?? 'UTC', tzNote: null as string | null };
    }
    const g = groups.data?.find((x) => x.id === parsedTarget.rawId);
    const zones = [
      ...new Set(
        (g?.screen_ids ?? [])
          .map((id) => screens.data?.find((s) => s.id === id)?.timezone)
          .filter((z): z is string => !!z),
      ),
    ];
    const first = zones[0] ?? 'UTC';
    return {
      tz: first,
      tzNote:
        zones.length > 1 ? `Screens in ${zones.length} timezones — showing times in ${first}.` : null,
    };
  }, [parsedTarget, screens.data, groups.data]);

  // Reconcile the client view against the server resolver for the current hour
  // (only for a single screen — that is what /preview evaluates).
  const previewScreenId = parsedTarget?.kind === 'screen' ? parsedTarget.rawId : undefined;
  const preview = useSchedulePreview(view === 'week' ? previewScreenId : undefined);
  const reconcile = useMemo<'match' | 'differ' | null>(() => {
    if (!preview.data || !previewScreenId) return null;
    const now = zonedNow(tz);
    const mine = winnerScheduleAt(calendarSources, now.weekday, now.minutes);
    const server = preview.data.candidates.find((c) => c.won)?.schedule_id ?? null;
    return mine === server ? 'match' : 'differ';
  }, [preview.data, previewScreenId, tz, calendarSources]);

  const editing = search.edit ? schedules.data?.find((s) => s.id === search.edit) : undefined;
  const formOpen = search.create || !!editing || !!slotInitial;

  const openSlotCreate = (day: number, startMin: number, endMin: number) => {
    if (!parsedTarget) return;
    const to = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    setSlotInitial({
      targetType: parsedTarget.kind,
      targetId: parsedTarget.rawId,
      daysMask: 1 << day,
      startTime: to(startMin),
      endTime: endMin >= 1440 ? '' : to(endMin),
    });
  };

  const closeForm = () => {
    setSlotInitial(null);
    setSearch({ create: undefined, edit: undefined });
  };

  const loading = schedules.isLoading || screens.isLoading || groups.isLoading || playlists.isLoading;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Schedules</h1>
          <div className="subtitle">When each playlist plays, and where.</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <div className="seg" role="tablist" aria-label="Schedule view">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'table'}
              className={view === 'table' ? 'active' : ''}
              onClick={() => setView('table')}
            >
              Table
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'week'}
              className={view === 'week' ? 'active' : ''}
              onClick={() => setView('week')}
            >
              Week
            </button>
          </div>
          {perms.canManageContent && (
            <Button variant="primary" onClick={() => setSearch({ create: true })}>
              New schedule
            </Button>
          )}
        </div>
      </div>

      {view === 'week' && (
        <div className="weekcal-picker-row">
          <span className="text-dim" style={{ fontSize: 'var(--fs-sm)' }}>
            Showing
          </span>
          <TargetPicker options={targetOptions} value={target} onChange={setTarget} />
        </div>
      )}

      {loading ? (
        <Loading />
      ) : schedules.isError ? (
        <ErrorState message="Could not load schedules." onRetry={() => schedules.refetch()} />
      ) : view === 'week' ? (
        !parsedTarget ? (
          <div className="card card-pad">
            <EmptyState icon={<ScheduleIcon />} title="Pick a screen or group">
              <p>Choose a target above to see its week at a glance.</p>
            </EmptyState>
          </div>
        ) : (
          <WeekCalendar
            sources={calendarSources}
            tz={tz}
            tzNote={tzNote}
            reconcile={reconcile}
            readOnly={!perms.canManageContent}
            onEditSchedule={(id) => setSearch({ edit: id })}
            onCreateSlot={openSlotCreate}
          />
        )
      ) : (schedules.data ?? []).length === 0 ? (
        <div className="card card-pad">
          <SchedulesEmpty
            canManage={perms.canManageContent}
            hasScreens={(screens.data ?? []).length > 0}
            hasPlaylists={(playlists.data ?? []).length > 0}
            onCreate={() => setSearch({ create: true })}
            onGoToScreens={() => navigate({ to: '/screens', search: { pair: true } })}
            onGoToPlaylists={() => navigate({ to: '/playlists' })}
          />
        </div>
      ) : (
        // One table for every scope group, not one per group (UX audit P2-3 —
        // the repeated Playlist/When/Priority/Enabled header read as noise). Each
        // bucket becomes its own <tbody> with a spanning label row instead of its
        // own <thead>, so the column header appears exactly once.
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Playlist</th>
                <th>When</th>
                <th>
                  Priority <span className="text-dim" style={{ fontWeight: 400 }}>(higher wins)</span>
                </th>
                <th>Enabled</th>
                <th />
              </tr>
            </thead>
            {buckets.map((bucket) => (
              <tbody key={bucket.label}>
                <tr className="table-group-row">
                  <td colSpan={5}>{bucket.label}</td>
                </tr>
                {bucket.rows.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-strong">
                      {playlistName(s.playlist_id)}
                      {s.layout_id && (
                        <span
                          className="sched-layout-glyph"
                          title={`Layout override: ${layoutName(s.layout_id)}`}
                          aria-label={`Layout override: ${layoutName(s.layout_id)}`}
                        >
                          <ZoneGlyph />
                        </span>
                      )}
                    </td>
                    <td>{scheduleWindowSummary(s)}</td>
                    <td>
                      <Badge tone="neutral">{s.priority}</Badge>
                    </td>
                    <td>
                      {perms.canManageContent ? (
                        <label className="row">
                          <input
                            type="checkbox"
                            checked={s.enabled}
                            onChange={(e) =>
                              update.mutate(
                                { id: s.id, body: { enabled: e.target.checked } },
                                { onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed') },
                              )
                            }
                          />
                        </label>
                      ) : s.enabled ? (
                        <Badge tone="ok">On</Badge>
                      ) : (
                        <Badge tone="neutral">Off</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {perms.canManageContent && (
                        <div className="row" style={{ justifyContent: 'flex-end' }}>
                          <Button size="sm" onClick={() => setSearch({ edit: s.id })}>
                            Edit
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setConfirmDelete(s.id)}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {formOpen && (
        <ScheduleForm schedule={editing} initial={slotInitial ?? undefined} onClose={closeForm} />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete schedule?"
          message="Screens covered by this schedule will fall back to the next matching one."
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            del.mutate(confirmDelete, {
              onSuccess: () => {
                toast.success('Schedule deleted');
                setConfirmDelete(null);
              },
              onError: (e) => {
                setConfirmDelete(null);
                toast.error(e instanceof ApiError ? e.message : 'Failed');
              },
            })
          }
        />
      )}
    </div>
  );
}

/** A small multi-zone glyph marking a schedule that carries a layout override —
 *  same round-stroke language as NavIcons; the highlighted side zone reads as
 *  "this isn't plain fullscreen." Decorative; the title/aria live on the wrapper. */
function ZoneGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M15 4v16" />
      <rect x="15.5" y="4.5" width="5" height="15" rx="1" fill="currentColor" opacity="0.25" stroke="none" />
    </svg>
  );
}

/**
 * State-aware empty state: schedules put playlists on screens, so the
 * missing prerequisite (a screen, then a playlist) is what the org needs
 * to hear about first — not "create a schedule" against nothing to target.
 */
function SchedulesEmpty({
  canManage,
  hasScreens,
  hasPlaylists,
  onCreate,
  onGoToScreens,
  onGoToPlaylists,
}: {
  canManage: boolean;
  hasScreens: boolean;
  hasPlaylists: boolean;
  onCreate: () => void;
  onGoToScreens: () => void;
  onGoToPlaylists: () => void;
}) {
  if (!hasScreens) {
    return (
      <EmptyState icon={<ScheduleIcon />} title="Pair a screen first">
        <p>Schedules put playlists on screens — there&apos;s nothing to schedule until a screen exists.</p>
        {canManage && (
          <div className="mt-4">
            <Button variant="primary" onClick={onGoToScreens}>
              Pair a screen
            </Button>
          </div>
        )}
      </EmptyState>
    );
  }

  if (!hasPlaylists) {
    return (
      <EmptyState icon={<ScheduleIcon />} title="Build a playlist first">
        <p>Schedules assign a playlist to a screen, group, or the whole org — create a playlist to schedule.</p>
        {canManage && (
          <div className="mt-4">
            <Button variant="primary" onClick={onGoToPlaylists}>
              Go to Playlists
            </Button>
          </div>
        )}
      </EmptyState>
    );
  }

  return (
    <EmptyState icon={<ScheduleIcon />} title="No schedules yet">
      <p>Assign a playlist to a screen, group, or the whole org with a day/time window.</p>
      {canManage && (
        <div className="mt-4">
          <Button variant="primary" onClick={onCreate}>
            New schedule
          </Button>
        </div>
      )}
    </EmptyState>
  );
}
